import * as React from 'react'
import { UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import * as Debounce from 'react-debounce-input'
import Select from 'react-select'
import { inject, observer } from 'mobx-react'
import { Link, browserHistory } from 'react-router'
import {
  ILocation,
  IMultiLocationInventory,
  InventorySearchFilter,
  API,
  INegativeQuantityObject,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import AppPagination from '../../../../components/Pagination/index'
import { FilterDropdown } from '../../../../components/FilterDropdown/index'
import AppHeader from '../../../../components/AppView/Header'
import { AppView, AppMainView } from '../../../../components/AppView/index'
import { Routes } from '../../../../constants/Routes'
import { ExportInventoryButton } from './ExportInventoryButton'
import { ActiveInventoryTable } from './ActiveInventoryTable'
import { LowStockModal } from './LowStockModal'
import { QueryFilter } from './QueryFilter'
import { LocalStorage } from '../../../../utilities/LocalStorage'
import { NegativeInventoryAlert } from './NegativeInventoryAlert'
import { Skeleton, Button, Alert } from 'antd'
import { NoPermissionsResult } from '../../../../components/NoPermissionsResult'
import ButtonGroup from 'antd/lib/button/button-group'
import { HelpLinkMenu } from '../../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import Helmet from 'react-helmet'
import { getCategoryName } from '../../../../utilities/helpers'
import { HelpLinks } from '../../../../constants/ExternalLinks'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  location: any
}

interface State {
  total: number
  perPage: number
  multiLocationInventories?: IMultiLocationInventory[]
  visibleMultiLocationInventories?: IMultiLocationInventory[]
  showCategory: boolean
  showBrand: boolean
  showLowStockQuantity: boolean
  showLowStockModal: boolean
  showOnlyInStock: boolean
  inventoryLocations: ILocation[]
  negativeInventoryProductList: INegativeQuantityObject[]
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class DashboardViewInventory extends React.Component<Props, State> {
  private perPageInput?: HTMLInputElement

  queryFilter: QueryFilter

  widthBreakPoint = 844

  constructor(props: Props) {
    super(props)
    this.queryFilter = new QueryFilter(browserHistory)
    this.state = {
      total: 0,
      perPage: this.queryFilter.inventoryFilter.limit,
      showCategory: true,
      showBrand: true,
      showLowStockQuantity: false,
      showLowStockModal: false,
      showOnlyInStock: LocalStorage.getInventoryFilterEmptyQtyHidden(),
      inventoryLocations: [],
      negativeInventoryProductList: [],
    }
  }

  async componentDidMount() {
    this.props.currentCompanyStore!.productStore!.getCategories()
    this.props.currentCompanyStore!.supplierStore!.getSuppliers()

    const locations = this.getViewInventoryLocations()
    const categories =
      this.props.currentCompanyStore?.productStore?.flattenedCategories.map((category) => {
        return {
          ...category,
          name: getCategoryName(category),
        }
      }) || []
    const { selectedLocationIds, selectedCategoryIds } = this.queryFilter.inventoryFilter

    if (selectedLocationIds.length === 0) {
      const allLocationIds = this.getLocationIds(locations)
      this.queryFilter.setSelectedLocationIds(allLocationIds)
    }
    if (selectedCategoryIds.length === 0) {
      const allCategoryIds = this.getCategoryIds(categories)
      this.queryFilter.setSelectedCategoryIds(allCategoryIds)
    }
    await this.setState({
      inventoryLocations: locations,
    })

    await this.getNegativeInventoryProductList()
    await this.getInventory(false)
  }

  getNegativeInventoryProductList = async () => {
    const { selectedLocationIds } = this.queryFilter.inventoryFilter
    this.setState({ negativeInventoryProductList: [] })
    const company = this.props.currentCompanyStore!.company!

    const negativeInventoryResult: INegativeQuantityObject[] =
      await API.getNegativeInventoryProductList(company.id, selectedLocationIds)
    this.setState({
      negativeInventoryProductList: negativeInventoryResult,
    })
  }

  private getViewInventoryLocations(): ILocation[] {
    const allLocations = this.props.currentCompanyStore!.locations || []
    let locationsToShow = allLocations || []
    locationsToShow = locationsToShow.filter((location) => {
      // Check to see that the user has view inventory permissions at that location
      // This is because a user can be assigned to multiple locations, but do not necessarily have permissions to view at every one
      return this.props.currentUserStore!.hasPermissionAtEntity(
        PermissionNames.CAN_VIEW_INVENTORY,
        location.id,
      )
    })
    return locationsToShow
  }

  getLocationIds = (inventoryLocations: ILocation[]): number[] => {
    return inventoryLocations.map((location) => location.id)
  }

  getCategoryIds = (categories: any[]): string[] => {
    return categories.map((category) => category.id)
  }

  getInventory = async (resetOffset = true) => {
    const company = this.props.currentCompanyStore!.company!

    // Refresh the negative inventory list for every fetch
    await this.getNegativeInventoryProductList()

    if (resetOffset) {
      this.queryFilter.inventoryFilter.offset = 0
    }

    const searchFilters = this.getInventorySearchFilter()

    await this.setState({ multiLocationInventories: undefined })
    const { multiLocationInventory, total, limit } = await API.getInventoryByMultipleLocations(
      company.id,
      searchFilters,
    )

    this.setState({
      total,
      perPage: limit,
      multiLocationInventories: multiLocationInventory,
    })

    this.queryFilter.apply()
  }

  private getInventorySearchFilter(): InventorySearchFilter {
    const { selectedLocationIds, selectedCategoryIds } = this.queryFilter.inventoryFilter

    const locationIds = selectedLocationIds.length
      ? selectedLocationIds
      : this.getLocationIds(this.state.inventoryLocations)

    // Create a new object by adding "hideEmptyQuantities"
    const inventoryFilterWithHideEmptyQuantities = {
      sku: undefined,
      locationIds,
      ...this.queryFilter.inventoryFilter,
      hideEmptyQuantities: LocalStorage.getInventoryFilterEmptyQtyHidden(),
    }
    return new InventorySearchFilter(inventoryFilterWithHideEmptyQuantities)
  }

  private async handlePaginationClick(data: any) {
    const selected = data.selected
    const offset = Math.ceil(selected * this.state.perPage)
    this.queryFilter.setOffset(offset)
    this.getInventory(false)
  }

  handleClickOnBtnShowAll(showOnlyInStock: boolean) {
    LocalStorage.setInventoryFilterEmptyQtyHidden(showOnlyInStock)
    this.setState({ showOnlyInStock })
    this.getInventory()
  }

  setProductLimit(showOnlyInStock: boolean) {
    LocalStorage.setInventoryFilterEmptyQtyHidden(showOnlyInStock)
    this.getInventory()
  }

  updateSelectedLocationIds = async (selectedLocationId: string) => {
    const locationId = parseInt(selectedLocationId, 10)
    this.queryFilter.toggleSelectedLocationIds(locationId)
    this.getInventory()
  }

  updateSelectedCategoryIds = async (selectedCategoryId: string) => {
    this.queryFilter.toggleSelectedCategoryIds(selectedCategoryId)
    this.getInventory()
  }

  selectAllLocationOptions = () => {
    const locationsToShow = this.getViewInventoryLocations()
    const allOptionIds = locationsToShow.map((option) => option.id)
    this.queryFilter.setSelectedLocationIds(allOptionIds)
    this.getInventory()
  }

  selectAllCategoryOptions = () => {
    const categories = this.props.currentCompanyStore!.productStore!.flattenedCategories.map(
      (category) => {
        return {
          ...category,
          name: getCategoryName(category),
        }
      },
    )
    const allOptionIds = categories.map((option) => option.id)
    this.queryFilter.setSelectedCategoryIds(allOptionIds)
    this.getInventory()
  }

  setPagination(limit: number) {
    this.queryFilter.setOffset(0)
    this.queryFilter.setLimit(limit)
    this.getInventory()
  }

  unselectAllLocationOptions = () => {
    this.queryFilter.setSelectedLocationIds([])
    this.getInventory()
  }

  unselectAllCategoryOptions = () => {
    this.queryFilter.setSelectedCategoryIds([])
    this.getInventory()
  }

  render() {
    const { selectedLocationIds, selectedCategoryIds } = this.queryFilter.inventoryFilter
    const company = this.props.currentCompanyStore!.company!
    const canViewInventoryAtAnyLocation = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_INVENTORY,
      company.id,
    )

    // If the user doesn't have view inventory permissions anywhere, show a warning message
    if (!canViewInventoryAtAnyLocation) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_INVENTORY} />
    }

    const hasEditProductPermissions = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_PRODUCTS,
      company.id,
    )

    const locationsToShow = this.getViewInventoryLocations()
    const categories = this.props.currentCompanyStore!.productStore!.flattenedCategories.map(
      (category) => {
        return {
          ...category,
          name: getCategoryName(category),
        }
      },
    )
    const suppliers = this.props.currentCompanyStore!.supplierStore!.suppliersWithName
    const multiLocationInventories = this.state.multiLocationInventories

    // Required data
    if (!locationsToShow || !categories || !suppliers) {
      return <Skeleton active />
    }

    // Pagination
    const { inventoryFilter } = this.queryFilter
    const { total, perPage, showCategory, showBrand, negativeInventoryProductList } = this.state
    const pageCount = Math.ceil(total / perPage)
    const currentPage = Math.ceil(inventoryFilter.offset / perPage)

    const pagination = (
      <AppPagination
        currentPage={currentPage}
        pageCount={pageCount}
        onPageChange={this.handlePaginationClick.bind(this)}
      />
    )

    return (
      <AppView column>
        <Helmet>
          <title>Inventory | Overview</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader title='Inventory'>
          <span className='spaced-button-wrapper'>
            <HelpLinkMenu
              helpLinkMenus={[
                {
                  href: HelpLinks.ADDING_INVENTORY_VIA_AUDIT,
                  title: 'Auditing inventory',
                },
                {
                  href: HelpLinks.PURCHASE_ORDERS,
                  title: 'Adding purchase orders',
                },
              ]}
            />
            {this.props.currentCompanyStore?.canViewInventorySnapshots && (
              <BlazeButton
                buttonProps={{
                  icon: 'camera',
                  onClick: () =>
                    browserHistory.push(Routes.DASHBOARD_INVENTORY_SNAPSHOTS(company.id)),
                }}
                titleProps={{
                  title: 'Snapshots',
                  widthBreakPoint: this.widthBreakPoint,
                }}
              />
            )}

            <BlazeButton
              buttonProps={{
                icon: 'edit',
                onClick: () => this.setState({ showLowStockModal: true }),
              }}
              titleProps={{
                title: 'Bulk update low stock',
                widthBreakPoint: this.widthBreakPoint,
              }}
            />

            <ExportInventoryButton
              showCategory={showCategory}
              showBrand={showBrand}
              selectedLocationIds={selectedLocationIds}
              selectedCategoryIds={selectedCategoryIds}
              searchFilters={this.getInventorySearchFilter()}
              widthBreakPoint={this.widthBreakPoint}
            />
          </span>
        </AppHeader>
        <AppMainView>
          <div className='d-flex flex-wrap mb-2'>
            <div className='mr-2'>
              <div className='input-group mb-2'>
                <div className='input-group-prepend'>
                  <div className='input-group-text'>
                    <span className='fa fa-search' />
                  </div>
                </div>
                <Debounce
                  className='form-control'
                  value={this.queryFilter.inventoryFilter.searchQuery}
                  placeholder='Name, sku, ID'
                  debounceTimeout={500}
                  onChange={(e: any) => {
                    this.queryFilter.inventoryFilter.searchQuery = e.target.value
                    this.getInventory()
                  }}
                />
              </div>
            </div>

            <div className='mr-2 dropdown__location'>
              <FilterDropdown
                name='Categories'
                options={categories}
                selectedOptionIds={selectedCategoryIds}
                onSelect={this.updateSelectedCategoryIds}
                selectAllOptions={this.selectAllCategoryOptions}
                unselectAllOptions={this.unselectAllCategoryOptions}
              />
            </div>

            <div className='mr-2' style={{ width: 200 }}>
              <Select
                placeholder='Select a brand'
                clearable={true}
                value={inventoryFilter.selectedSupplierId}
                options={suppliers.map((supplier) => {
                  return {
                    value: supplier.id,
                    label: supplier.name,
                  }
                })}
                onChange={(option: { label: string; value: number } | null) => {
                  if (!option) {
                    inventoryFilter.selectedSupplierId = undefined
                  } else {
                    inventoryFilter.selectedSupplierId = option.value
                  }
                  this.getInventory()
                }}
              />
            </div>

            <div className='mr-2 dropdown__location'>
              <FilterDropdown
                name='Locations'
                options={locationsToShow.map((location) => ({
                  id: location.id.toString(),
                  name: location.name,
                }))}
                selectedOptionIds={selectedLocationIds.map((id) => id.toString())}
                onSelect={this.updateSelectedLocationIds}
                selectAllOptions={this.selectAllLocationOptions}
                unselectAllOptions={this.unselectAllLocationOptions}
              />
            </div>

            <div className='mr-2'>
              <UncontrolledButtonDropdown style={{ width: '100%' }}>
                <DropdownToggle caret color='light' style={{ textAlign: 'left' }}>
                  {this.state.showOnlyInStock ? 'Show in-stock' : 'Show all stock'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => this.handleClickOnBtnShowAll(true)}>
                    Show in-stock
                  </DropdownItem>
                  <DropdownItem onClick={() => this.handleClickOnBtnShowAll(false)}>
                    Show all stock
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            </div>

            <div className='mr-2'>
              <UncontrolledButtonDropdown style={{ width: '100%' }}>
                <DropdownToggle caret color='light' style={{ textAlign: 'left' }}>
                  Table columns
                </DropdownToggle>
                <DropdownMenu style={{ minWidth: '15rem' }}>
                  <div className='p-2'>
                    <div
                      className='ml-3 mr-2 p-2 cursor-pointer'
                      onClick={() => this.setState({ showCategory: !this.state.showCategory })}
                    >
                      <input
                        className='d-inline mr-2'
                        type='checkbox'
                        checked={this.state.showCategory}
                      />
                      <label style={{ display: 'inline', cursor: 'pointer' }}>Category</label>
                    </div>

                    <div
                      className='ml-3 mr-2 p-2 cursor-pointer'
                      onClick={() => this.setState({ showBrand: !this.state.showBrand })}
                    >
                      <input
                        className='d-inline mr-2'
                        type='checkbox'
                        checked={this.state.showBrand}
                      />
                      <label style={{ display: 'inline', cursor: 'pointer' }}>Brand</label>
                    </div>

                    <div
                      className='ml-3 mr-2 p-2 cursor-pointer'
                      onClick={() =>
                        this.setState({ showLowStockQuantity: !this.state.showLowStockQuantity })
                      }
                    >
                      <input
                        className='d-inline mr-2'
                        type='checkbox'
                        checked={this.state.showLowStockQuantity}
                      />
                      <label style={{ display: 'inline', cursor: 'pointer' }}>
                        Low stock level
                      </label>
                    </div>
                  </div>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            </div>

            <div className='mr-2'>
              <UncontrolledButtonDropdown>
                <DropdownToggle caret color='light'>
                  {inventoryFilter.limit} per page
                </DropdownToggle>
                <DropdownMenu className='shadow animated fadeIn faster'>
                  <DropdownItem onClick={() => this.setPagination(20)}>
                    <input
                      type='radio'
                      checked={inventoryFilter.limit === 20}
                      className='d-inline mr-2'
                    />
                    20 per page
                  </DropdownItem>
                  <DropdownItem onClick={() => this.setPagination(50)}>
                    <input
                      type='radio'
                      checked={inventoryFilter.limit === 50}
                      className='d-inline mr-2'
                    />
                    50 per page
                  </DropdownItem>
                  <DropdownItem onClick={() => this.setPagination(100)}>
                    <input
                      type='radio'
                      checked={inventoryFilter.limit === 100}
                      className='d-inline mr-2'
                    />
                    100 per page
                  </DropdownItem>
                  <DropdownItem onClick={() => this.setPagination(200)}>
                    <input
                      type='radio'
                      checked={inventoryFilter.limit === 200}
                      className='d-inline mr-2'
                    />
                    200 per page
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            </div>

            <div className='mr-2'>
              <button className='btn btn-light' onClick={() => this.getInventory()}>
                <span className='fa fa-refresh text-muted' />
              </button>
            </div>
          </div>

          <div className='d-flex mb-2'>
            {LocalStorage.getInventoryFilterEmptyQtyHidden() && (
              <div>
                <Alert
                  type='warning'
                  showIcon
                  style={{ width: 330, marginRight: 5 }}
                  message={
                    <div className='alert-main'>
                      Only showing products in-stock.{' '}
                      <a className='ml-3' onClick={() => this.handleClickOnBtnShowAll(false)}>
                        Show all
                      </a>
                    </div>
                  }
                />
              </div>
            )}
            {negativeInventoryProductList.length !== 0 && (
              <div>
                <NegativeInventoryAlert
                  negativeInventoryList={negativeInventoryProductList}
                  onClick={(productName) => {
                    this.queryFilter.inventoryFilter.searchQuery = productName
                    this.getInventory()
                  }}
                />
              </div>
            )}
          </div>

          <ActiveInventoryTable
            companyId={company.id}
            hasEditProductPermissions={hasEditProductPermissions}
            multiLocationInventories={multiLocationInventories}
            showCategory={this.state.showCategory}
            showBrand={this.state.showBrand}
            showLowStockQuantity={this.state.showLowStockQuantity}
            viewInventoryLocations={this.getViewInventoryLocations()}
            selectedLocationIds={selectedLocationIds}
            reload={() => this.getInventory(false)}
          />
          <div style={{ height: 20 }} />
        </AppMainView>
        <div>{pagination}</div>

        {this.state.showLowStockModal && (
          <LowStockModal
            onClose={() => this.setState({ showLowStockModal: false })}
            onSave={() => {
              this.getInventory(false)
            }}
          />
        )}
      </AppView>
    )
  }
}
